import { inject, Injectable, OnDestroy, signal } from "@angular/core";
import { Socket } from "ngx-socket-io";
import { Observable, Subscription } from "rxjs";
import { PingService } from "./ping.service";
import { HydrateService } from "./hydrate.service";
import { ArticleInfos } from "app/features/articles/models/article.model";
import { VersionDTO } from "../models/version_dto.model";
import { DialogsService } from "./dialogs/dialogs.service";
import { ConfirmationDialogComponent } from "../components/confirmation-dialog/confirmation-dialog.component";
import { ControleFacadeService } from "../../features/controle/services/store/controle-facade.service";
import { ProcessEtapeOpe } from "../../features/production/models/peo.model";
import { PeoFacadeService } from "../../features/production/services/peo/peo-facade.service";

@Injectable()
export class GeneralSocketService implements OnDestroy {
  private hydrateService = inject(HydrateService);
  private dialogService = inject(DialogsService);
  public pingService = inject(PingService);

  private controleFacade = inject(ControleFacadeService);
  private peoFacadeService = inject(PeoFacadeService);

  private socket: Socket | null = null;

  /**
   * Incrémentée à chaque CRÉATION d'instance socket (0 = pas encore de socket).
   *
   * Tout consommateur qui s'abonne par `fromEvent()` doit se réabonner à chaque
   * époque : `disconnect()` (logout) détruit l'instance — un abonnement pris sur
   * l'ancienne pointe dans le vide — et `start()` n'arrive qu'à la FIN de
   * `loadInitData()` — un composant construit avant (cas du rafraîchissement de
   * page, où le shell se rend pendant que l'init tourne) s'abonnerait dans le
   * vide aussi. C'est exactement le bug qui a rendu le temps réel du support
   * muet : l'effet du widget tournait une fois, échouait en silence, et ne se
   * ré-exécutait jamais.
   */
  readonly socketEpoch = signal(0);

  message$!: Observable<any>;
  refreshInfosArticle$!: Observable<ArticleInfos>;
  refreshVersion$!: Observable<VersionDTO>;
  peo$!: Observable<ProcessEtapeOpe>;
  controle$!: Observable<any>;

  private submessage?: Subscription;
  private subRefreshInfosArticle?: Subscription;
  private subRefreshVersion?: Subscription;
  private subControle?: Subscription;
  private subPeo?: Subscription;

  private version_dto: VersionDTO | null = null;
  private versionPrompted = false;

  fromEvent<T = any>(eventName: string): Observable<T> {
    if (!this.socket) {
      throw new Error(
        `[GeneralSocketService] Socket not initialized. Call start(token) before using fromEvent("${eventName}").`,
      );
    }

    return this.socket.fromEvent<T>(eventName);
  }

  constructor() {
    // 👀 rien ici côté socket
  }

  // ————————————————————————————
  // Création de la socket à partir du token
  // ————————————————————————————
  private initSocket(token: string): void {
    if (this.socket) {
      return; // déjà créée
    }

    const url = this.buildUrl();

    this.socket = new Socket({
      url,
      options: {
        path: "/ws/generalSocket",
        query: { token },
        transports: ["websocket"],
      },
    });

    // mapping des observables
    this.message$ = this.socket.fromEvent("messageLoading");
    this.refreshInfosArticle$ = this.socket.fromEvent<ArticleInfos>(
      "refreshInfosArticle",
    );
    this.refreshVersion$ = this.socket.fromEvent<VersionDTO>("refreshVersion");
    this.peo$ = this.socket.fromEvent<ProcessEtapeOpe>("peo");
    this.controle$ = this.socket.fromEvent("controle");

    // Événements connect / disconnect
    this.socket.on("disconnect", () => {
      // this.pingService._offline$.next(true);
    });

    this.socket.on("connect", () => {
      // this.pingService._offline$.next(false);
    });

    // Signale la nouvelle instance : les abonnés par époque se réabonnent.
    this.socketEpoch.update((v) => v + 1);
  }

  private buildUrl(): string {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${location.host}`;
  }

  // ————————————————————————————
  // API publique
  // ————————————————————————————

  /**
   * start = crée la socket (si besoin) + connect + subscriptions métier
   */
  start(token: string): void {
    this.initSocket(token);

    this.connect();
    this.initSubscriptions();
  }

  /**
   * connect = garantit que la WebSocket est ouverte
   * (pas de logique métier ici)
   */
  connect(): void {
    if (!this.socket) return;

    if (!this.socket.ioSocket?.connected) {
      this.socket.connect();
    }
  }

  /**
   * stop = enlève seulement les subscriptions métier
   * (la WebSocket peut rester ouverte)
   */
  stop(): void {
    this.submessage?.unsubscribe();
    this.subRefreshInfosArticle?.unsubscribe();
    this.subRefreshVersion?.unsubscribe();
    this.subControle?.unsubscribe();
    this.subPeo?.unsubscribe();

    this.submessage =
      this.subRefreshInfosArticle =
      this.subRefreshVersion =
      this.subControle =
      this.subPeo =
        undefined;
  }

  /**
   * disconnect = stop() + coupe la WebSocket + reset socket
   */
  disconnect(): void {
    this.stop();

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  // ————————————————————————————
  // Subscriptions métier
  // ————————————————————————————
  private initSubscriptions(): void {
    // on nettoie d'abord au cas où
    this.stop();
    if (!this.socket) return;

    this.subRefreshInfosArticle = this.refreshInfosArticle$.subscribe(
      (resp: ArticleInfos) => {
        try {
          if (resp) {
            this.hydrateService.hydrateOneInfoArticle(resp);
          }
        } catch (e) {
          console.error("no response on refresh infoArticle");
        }
      },
    );

    // TODO: désactivé temporairement — la logique "nouvelle version" est gérée côté front uniquement
    // this.subRefreshVersion = this.refreshVersion$.subscribe(
    //   (resp: VersionDTO) => { ... }
    // );

    this.submessage = this.message$.subscribe((resp: any) => {
      try {
        if (resp) {
          // traitement éventuel
        }
      } catch (e) {
        console.error("no response on message");
      }
    });

    this.subControle = this.controle$.subscribe((resp: any) => {
      try {
        if (resp) {
          this.controleFacade.store.upsertOne(resp);
        }
      } catch (e) {
        console.error("no response on controle");
      }
    });

    this.subPeo = this.peo$.subscribe((resp: ProcessEtapeOpe) => {
      try {
        if (resp) {
          this.peoFacadeService.store.upsertOne(resp);
        }
      } catch (e) {
        console.error("no response on peo");
      }
    });
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
